'use client'

import React, { useEffect, useState } from 'react'
import { useLocale } from 'next-intl'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material'

import { AirspaceAlert } from './airspaceAlert'

interface AlertData {
    alertLabel: string
    messages: string[]
    knowMore: string
    modalTitle: string
    okayText: string
    enable: boolean
}

const AirspaceAlertWrapper: React.FC = () => {
    const locale = useLocale()
    const [alertData, setAlertData] = useState<AlertData | null>(null)
    const [modalOpen, setModalOpen] = useState(false)
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768
    useEffect(() => {
        fetch('https://ui.cltp.co/json/airspace-alert.json')
            .then((res) => res.json())
            .then((data) => {
                if(!data.enable) return null
                const localeData = data[locale] || data['en']
                setAlertData(localeData)
            })
            .catch(() => {})
    }, [locale])

    if (!alertData) return null

    const fullMessage = alertData.messages.join('\n\n')

    return (
        <>
            <AirspaceAlert
                alertLabel={alertData.alertLabel}
                message={alertData.modalTitle}
                knowMoreText={alertData.knowMore}
                onKnowMore={() => setModalOpen(true)}
                isMobile={isMobile}
            />
            <Dialog
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                maxWidth="sm"
                fullWidth
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: 3,
                            mx: { xs: 2, sm: 3 },
                            my: { xs: 2, sm: 4 },
                            width: { xs: 'calc(100vw - 32px)', sm: 'auto' },
                            maxWidth: { xs: 'calc(100vw - 32px)', sm: '500px' },
                        }
                    }
                }}
            >
                <DialogTitle sx={{ pb: 2, pt: 3, px: 3 }}>
                    <Typography
                        sx={{
                            fontWeight: 600,
                            color: 'textColors.primary',
                            textAlign: 'center',
                            fontSize: '14px',
                        }}
                    >
                        {alertData.modalTitle}
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ pt: 0, pb: 3, px: 3 }}>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'textColors.secondary',
                            fontSize: '12px',
                            lineHeight: 1.6,
                            textAlign: 'center',
                            whiteSpace: 'pre-line',
                        }}
                    >
                        {fullMessage}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center' }}>
                    <Button
                        variant="customVariantPrimary"
                        onClick={() => setModalOpen(false)}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 500,
                            bgcolor: 'brand.primary',
                            color: 'brand.primaryForeground',
                            py: 1.25,
                            fontSize: '14px',
                            width: '100%',
                            '&:hover': {
                                bgcolor: 'brand.primaryHover',
                            }
                        }}
                    >
                        {alertData.okayText}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export { AirspaceAlertWrapper }
export default AirspaceAlertWrapper
